import { useMemo, useState } from "react";
import config from "../../../config";
import { ICAL_PATH } from "../constants";
import type { Classroom } from "../types";
import { entityId } from "../utils";

interface UseCalendarSubscriptionsArgs {
  selectedSchoolYearUuid: string;
  yearClassrooms: Classroom[];
  onCopyFailed: () => void;
}

export const useCalendarSubscriptions = ({
  selectedSchoolYearUuid,
  yearClassrooms,
  onCopyFailed,
}: UseCalendarSubscriptionsArgs) => {
  const [selectedClassroomForIcal, setSelectedClassroomForIcal] = useState("");
  const [copiedSchoolLink, setCopiedSchoolLink] = useState(false);
  const [copiedClassroomLink, setCopiedClassroomLink] = useState(false);
  const [subscriptionsOpen, setSubscriptionsOpen] = useState(false);

  const calendarLink = useMemo(() => {
    const configuredBase = config?.apiBaseUrl || "";
    if (!configuredBase) return "/api/v1/calendar/school.ics";
    return `${configuredBase.replace(/\/+$/, "")}${ICAL_PATH}`;
  }, []);

  const classroomCalendarLink = useMemo(() => {
    if (!selectedClassroomForIcal) return "";

    const configuredBase = config?.apiBaseUrl || "";
    const path = `/calendar/classrooms/${encodeURIComponent(selectedClassroomForIcal)}.ics`;

    if (!configuredBase) return `/api/v1${path}`;
    return `${configuredBase.replace(/\/+$/, "")}${path}`;
  }, [selectedClassroomForIcal]);

  const ensureSelectedClassroomForIcal = () => {
    if (!selectedSchoolYearUuid) {
      setSelectedClassroomForIcal("");
      return;
    }

    setSelectedClassroomForIcal((current) => {
      if (
        current &&
        yearClassrooms.some(
          (classroom) => String(entityId(classroom)) === String(current),
        )
      ) {
        return current;
      }

      return String(entityId(yearClassrooms[0]) || "");
    });
  };

  const copyCalendarLink = async () => {
    try {
      await navigator.clipboard.writeText(calendarLink);
      setCopiedSchoolLink(true);
      setTimeout(() => setCopiedSchoolLink(false), 1800);
    } catch {
      onCopyFailed();
    }
  };

  const copyClassroomCalendarLink = async () => {
    if (!classroomCalendarLink) return;

    try {
      await navigator.clipboard.writeText(classroomCalendarLink);
      setCopiedClassroomLink(true);
      setTimeout(() => setCopiedClassroomLink(false), 1800);
    } catch {
      onCopyFailed();
    }
  };

  return {
    calendarLink,
    classroomCalendarLink,
    selectedClassroomForIcal,
    setSelectedClassroomForIcal,
    copiedSchoolLink,
    setCopiedSchoolLink,
    copiedClassroomLink,
    setCopiedClassroomLink,
    subscriptionsOpen,
    setSubscriptionsOpen,
    copyCalendarLink,
    copyClassroomCalendarLink,
    ensureSelectedClassroomForIcal,
  };
};
